import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { gsap, Power3 } from 'gsap'
import { useStaticQuery, graphql, navigate } from 'gatsby'

import { fonts, colors } from '../../styles/variables'
import { media } from '../../styles/util'

const Container = styled.div`
  position: relative;
  z-index: 200;

  display: flex;
  place-items: center;

  width: 100%;

  background-color: white;
  display: grid;
  grid-template-columns: 320px 1fr;
  .small-nav {
    background-color: #f9f9f9;
    width: 100%;
    height: 100%;

    ${media.tablet`
      padding: 20px;
    `}

    ${media.desktop`
      padding: 40px;
    `}

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 20px;

      li {
        padding: 0;
        margin: 0;
        opacity: 0;

        a {
          font-family: ${fonts.larsseit.medium};
          font-size: 24px;
          color: ${colors.navy};
          text-decoration: none;
          display: block;
          transition: 100ms all ease-in-out;
          &[aria-current='page'] {
            color: ${colors.orange};
          }
        }
      }
    }
  }

  .img-nav {
    ${media.tablet`
      display: grid;
      padding: 17px;
      grid-column-gap: 17px;
      grid-template-columns: auto auto auto;
    `}

    ${media.desktop`
      display: flex;
      gap: 17px;
    `}

    a {
      overflow: hidden;
      border-radius: 6px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      opacity: 0;

      span {
        background-color: ${colors.navy};
        display: block;
        width: 100%;
        padding: 5px;
        text-align: center;
        transform: translateY(-20px);
        border-bottom-left-radius: 6px;
        border-bottom-right-radius: 6px;
        font-family: ${fonts.larsseit.medium};
        color: #fff;
      }

      img {
        height: 170px;
        width: 170px;
        object-fit: cover;
      }
    }
  }
`

const query = graphql`
  query {
    bestsellers: file(relativePath: { regex: "/navigation/bestsellers.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 180)
      }
    }
    supplements: file(relativePath: { regex: "/navigation/supplements.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 180)
      }
    }
    powders: file(relativePath: { regex: "/navigation/powders.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 180)
      }
    }
    pawcare: file(relativePath: { regex: "/navigation/pawcare.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 180)
      }
    }
    bundles: file(relativePath: { regex: "/navigation/bundles.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 180)
      }
    }
    shopall: file(relativePath: { regex: "/navigation/all.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 180)
      }
    }
    accessories: file(relativePath: { regex: "/navigation/accessories.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 180)
      }
    }
    dental: file(relativePath: { regex: "/navigation/dental-chews.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 180)
      }
    }
  }
`

const LargeNav = ({ visible, onClickOutside }) => {
  const ref = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClickOutside && onClickOutside()
      }
    }
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [onClickOutside])

  const {
    bestsellers,
    supplements,
    powders,
    pawcare,
    bundles,
    shopall,
    candles,
    accessories,
    dental
  } = useStaticQuery(query)

  const navItems = [
    { label: 'Best Sellers', url: '/products/bestsellers', external: false, image: bestsellers },
    { label: 'Supplements', url: '/products/supplements', external: false, image: supplements },
    { label: 'Toppers', url: '/products/toppers', external: false, image: powders },
    { label: 'Dental', url: '/products/dental', external: false, image: dental },
    //{ label: 'Candles', url: '/products/puppy-breath-candle', external: false, image: candles },
    { label: 'Grooming', url: '/products/grooming', external: false, image: pawcare },
    { label: 'Accessories', url: '/products/accessories', external: false, image: accessories },
    { label: 'Bundles', url: '/products/bundles', external: false, image: bundles },
    { label: 'Shop All', url: '/products/', external: false, image: shopall }
  ]

  useEffect(() => {
    gsap.to('.small-nav ul li', {
      ease: Power3.easeOut,
      opacity: 1,
      duration: 1,
      y: 0,
      stagger: 0.1
    })
    gsap.to('.img-nav a', {
      ease: Power3.easeOut,
      opacity: 1,
      duration: 1,
      y: 0,
      stagger: 0.1
    })
  }, [visible])

  return (
    <Container ref={ref}>
      {visible && (
        <>
          <div className="small-nav">
            <ul>
              {navItems.map((item, index) => (
                <li key={index}>
                  <Link to={item.url}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="img-nav">
            {navItems.map((item, index) => (
              <Link to={item.url} key={index}>
                <img
                  src={item.image.childImageSharp.gatsbyImageData.images.fallback.src}
                  alt={item.label}
                />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </Container>
  )
}

export default LargeNav
